// Plan definition + a hook exposing the current user's plan state.
//
// Four paid tiers + admin (staff-only) + beta (legacy — grandfathered
// invitees). The backend GET /profile returns the plan id; the tier
// catalog here (TIERS) is the source of truth for display metadata
// (price, storage, included frames). Backend authoritative on the
// current tier and the balance; frontend just renders.
//
// Credit pricing shape: non-drizzle ≤500 frames = 0 credits; non-drizzle
// >500 = (frames − 500) credits; any drizzle = frames × 2 credits. Source
// of truth: computeCredits() in services/instances.ts (mirrors
// lambda-start-job/ledger.go). See project_pricing_shape memory.

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProfile } from './api';

export type PlanId =
  | 'first-light'   // free tier
  | 'full-stack'    // $15/mo
  | 'tall-stack'    // $49/mo
  | 'beta'          // legacy — grandfathered invite tier
  | 'admin';        // staff — unlimited

/** True when the plan unlocks pro-tier fan-out (Split axis fan-out,
 *  Panels fan-out under a shared BatchID). Mirrors canFanOutStacks in
 *  lambda-start-job/ledger.go — backend enforces the same rule with a
 *  403 PRO_FEATURE_REQUIRED. Beta included: grandfathered founder
 *  pricing keeps early users on the paid features they helped test. */
export const canFanOutStacks = (id: string | null | undefined): boolean =>
  id === 'admin' || id === 'full-stack' || id === 'tall-stack' || id === 'beta';

const GB = 1024 * 1024 * 1024;

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  /** Monthly USD price. 0 for free / legacy / admin. */
  monthlyUSD: number;
  /** Storage quota in bytes. Infinity for admin. */
  storageBytes: number;
  /** Monthly frame allowance included in the tier. Infinity for admin. */
  monthlyFrames: number;
  /** Marketing sub-copy — used on the upgrade picker. */
  perkLine: string;
}

// Tier catalog — source of truth for display; the same metadata will
// be mirrored to Stripe products/prices (Stripe stores IDs; this table
// keeps the human-facing shape in one place).
export const FIRST_LIGHT_PLAN: Plan = {
  id: 'first-light',
  name: 'First Light',
  tagline: 'Free to try — see if the workflow fits.',
  monthlyUSD: 0,
  storageBytes: 10 * GB,
  monthlyFrames: 1000,
  perkLine: '10 GB · 1,000 frames/month · every feature',
};

export const FULL_STACK_PLAN: Plan = {
  id: 'full-stack',
  name: 'Full Stack',
  tagline: 'Serious deep-sky, mosaic sessions.',
  monthlyUSD: 15,
  // Cap sized to hit ≥1.3× worst-case margin at S3 Standard pricing.
  // Bumps back up once deep-archive lifecycle transitions ship.
  storageBytes: 300 * GB,
  monthlyFrames: 12000,
  perkLine: '300 GB · 12,000 frames/month · top-ups available',
};

export const TALL_STACK_PLAN: Plan = {
  id: 'tall-stack',
  name: 'Tall Stack',
  tagline: 'Years of raws, multiple targets in rotation.',
  monthlyUSD: 49,
  // See FULL_STACK note — cap sized around S3 Standard economics.
  // Will bump to ~2 TB when deep-archive lifecycle is wired.
  storageBytes: 1000 * GB,
  monthlyFrames: 50000,
  perkLine: '1 TB · 50,000 frames/month · priority scheduling',
};

// Grandfathered invite plan — kept in the catalog so legacy users
// don't break when the backend still returns 'beta'. New users get
// 'first-light' instead.
export const BETA_PLAN: Plan = {
  id: 'beta',
  name: 'Closed Beta',
  tagline: 'Invite-only. Founder perks locked in.',
  monthlyUSD: 0,
  storageBytes: 400 * GB,
  monthlyFrames: 5000,
  perkLine: '400 GB · 5,000 frames/month · founder-locked pricing',
};

// Admin plan is a display-only marker. Backend derives it from the `admin`
// Cognito group and skips balance + storage-quota guards (see ledger.go /
// main.go / archive.go). Meters render ∞ instead of used/quota bars.
export const ADMIN_PLAN: Plan = {
  id: 'admin',
  name: 'Admin',
  tagline: 'Unlimited storage, unlimited stacking minutes.',
  monthlyUSD: 0,
  storageBytes: Infinity,
  monthlyFrames: Infinity,
  perkLine: 'Everything, unlimited.',
};

const PLANS: Record<PlanId, Plan> = {
  'first-light': FIRST_LIGHT_PLAN,
  'full-stack':  FULL_STACK_PLAN,
  'tall-stack':  TALL_STACK_PLAN,
  beta:  BETA_PLAN,
  admin: ADMIN_PLAN,
};

// Publicly-selectable tiers in display order (ascending price). Used by
// the plan-picker section on the Profile page. Excludes beta (legacy)
// and admin (staff-only).
export const PUBLIC_TIERS: Plan[] = [
  FIRST_LIGHT_PLAN,
  FULL_STACK_PLAN,
  TALL_STACK_PLAN,
];

// Top-up packs — one-time purchases that add non-expiring credits to
// the user's balance. Independent of tier — a First Light user can buy
// a Small pack the same as a Tall Stack user. Per-credit AWS cost is
// ~$0.0002 (see project_pipeline_baselines memory), so even the Bulk
// pack retains ≥4× margin at typical workload mix.
export interface TopupPack {
  id: string;
  name: string;
  priceUSD: number;
  frames: number;
  /** Effective price per 1000 frames — for the display sub-line. */
  perThousand: number;
  tagline: string;
}

export const TOPUP_PACKS: TopupPack[] = [
  {
    id: 'small',
    name: 'Sugar',
    priceUSD: 3,
    frames: 1000,
    perThousand: 3.00,
    tagline: 'A weekend session worth of stacking.',
  },
  {
    id: 'bulk',
    name: 'Butter',
    priceUSD: 10,
    frames: 5000,
    perThousand: 2.00,
    tagline: 'A busy month of clear nights — 33% off.',
  },
  {
    id: 'whale',
    name: 'Maple',
    priceUSD: 30,
    frames: 20000,
    perThousand: 1.50,
    tagline: 'Set it and forget it — 50% off. Never expires.',
  },
];

export interface UserPlanState {
  loading: boolean;
  error: string | null;
  /** null when the token has no plan claim we recognize — shouldn't happen
   *  behind RequireAuth (post-confirm Lambda adds free), but the hook stays
   *  honest about it. */
  plan: Plan | null;
  /** True when the caller's plan has no storage/credits cap (currently
   *  admin only). Meter components render ∞ when this is set. */
  isUnlimited: boolean;
  creditsBalance: number;
  creditsMonthlyGrant: number;
  /** ISO-8601 timestamp — start of next UTC month. */
  nextGrantAt: string | null;
  storageUsedBytes: number;
  storageWorkingBytes: number;
  storageArchivedBytes: number;
  /** Multiplier the backend applied to archivedBytes when computing
   *  usedBytes (0.70 today). Surfaced so the UI's split bar and the
   *  archived-legend annotation agree with server math. */
  storageArchiveDiscount: number;
  storageQuotaBytes: number;
}

const EMPTY: UserPlanState = {
  loading: true,
  error: null,
  plan: null,
  isUnlimited: false,
  creditsBalance: 0,
  creditsMonthlyGrant: 0,
  nextGrantAt: null,
  storageUsedBytes: 0,
  storageWorkingBytes: 0,
  storageArchivedBytes: 0,
  storageArchiveDiscount: 1,
  storageQuotaBytes: 0,
};

/**
 * Fetches the current user's plan + usage from GET /profile. That endpoint
 * lazy-grants the monthly stacking-minutes refill on the first call each
 * UTC month, so this hook is what pulls the grant trigger from the UI side.
 */
export const useUserPlan = (): UserPlanState => {
  const { getIdToken, isAuthenticated } = useAuth();
  const [state, setState] = React.useState<UserPlanState>(EMPTY);

  React.useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setState({ ...EMPTY, loading: false });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const token = await getIdToken();
        const resp = await getProfile({ token });
        if (cancelled) return;
        // Server is the authority on plan id — trust `resp.plan`. Fall back
        // to first-light (free) if the string is unrecognized (forwards-
        // compat for future plan tiers landing before this frontend redeploys).
        const plan = PLANS[resp.plan as PlanId] ?? FIRST_LIGHT_PLAN;
        setState({
          loading: false,
          error: null,
          plan,
          isUnlimited: plan.id === 'admin',
          creditsBalance: resp.credits.balance,
          creditsMonthlyGrant: resp.credits.monthlyGrant,
          nextGrantAt: resp.credits.nextGrantAt,
          storageUsedBytes: resp.storage.usedBytes,
          storageWorkingBytes: resp.storage.workingBytes,
          storageArchivedBytes: resp.storage.archivedBytes,
          storageArchiveDiscount: resp.storage.archiveDiscount,
          storageQuotaBytes: resp.storage.quotaBytes,
        });
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: (e as Error).message }));
      }
    })();
    return () => { cancelled = true; };
  }, [getIdToken, isAuthenticated]);

  return state;
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= GB) return `${(bytes / GB).toFixed(bytes % GB === 0 ? 0 : 1)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1)   return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1)   return `${kb.toFixed(0)} KB`;
  return `${bytes} B`;
};
